/**
 * Layout Component
 *
 * Main layout wrapper that combines Header, Navigation, Footer, and children.
 * Provides consistent structure across all pages.
 *
 * Theme-specific navigation:
 * - Mac Studio: Provides its own immersive chrome via MacStudioLayout
 * - Matrix Terminal: Uses CommandPalette (/ or : trigger), no generic header
 * - Terminal Explorer: Full-screen terminal interface (superadmin only)
 * - Other themes: Standard header + navigation + ThemeSwitcher
 *
 * Universal: Cmd+, opens SettingsModal on all themes
 */

import { ReactNode, useState, useCallback, useEffect } from 'react'
import { Header } from './Header'
import { Footer } from './Footer'
import { Navigation } from './Navigation'
import { Container } from '@/components/ui/Container'
import { ThemeSwitcher } from '@/components/theme/ThemeSwitcher'
import { useLayoutTheme } from '@/contexts/LayoutThemeContext'
import { WebTerminal, TerminalToggle } from '@/components/terminal'
import { useSession } from '@/lib/auth-client'
import { CommandPalette } from '@/components/navigation/CommandPalette'
import { SettingsModal } from '@/components/navigation/SettingsModal'

interface LayoutProps {
  children: ReactNode
  showNavigation?: boolean
  showThemeSwitcher?: boolean
}

export function Layout({
  children,
  showNavigation = true,
  showThemeSwitcher = true
}: LayoutProps) {
  const { themeConfig, setTheme } = useLayoutTheme()
  const { data: session } = useSession()
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)

  // Check if user is superadmin (musia@habitusnet.ch)
  const isSuperAdmin = session?.user?.email === 'musia@habitusnet.ch'

  // Theme-specific behavior flags
  const isMatrixTerminal = themeConfig.id === 'matrix-terminal'
  const isMacStudio = themeConfig.id === 'mac-studio'
  const isTerminalExplorer = themeConfig.id === 'terminal-explorer'

  // Callback for opening settings from CommandPalette
  const handleOpenSettings = useCallback(() => {
    setIsSettingsOpen(true)
  }, [])

  // Global Cmd+, keyboard shortcut for settings
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === ',') {
        e.preventDefault()
        setIsSettingsOpen(prev => !prev)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Terminal Explorer theme - full-screen terminal interface (superadmin only)
  if (isTerminalExplorer) {
    // Redirect non-superadmins back to default theme
    if (!isSuperAdmin) {
      setTheme('modern-cards')
      return null
    }

    return (
      <>
        <WebTerminal />
        <TerminalToggle
          isTerminalMode={true}
          onToggle={() => setTheme('modern-cards')}
        />
        <SettingsModal
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
        />
      </>
    )
  }

  // Mac Studio theme is fully immersive - bypass standard layout chrome
  // The MacStudioLayout component provides its own menu bar and controls
  if (isMacStudio) {
    return (
      <>
        {children}
        <SettingsModal
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
        />
      </>
    )
  }

  // Matrix Terminal theme - immersive with command palette navigation
  if (isMatrixTerminal) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#0a0a0a',
          color: '#0f0',
          fontFamily: "'Courier New', monospace",
        }}
      >
        {/* Command palette for navigation (triggered by / or :) */}
        <CommandPalette onOpenSettings={handleOpenSettings} />

        {/* Main content */}
        <main
          id="main-content"
          style={{
            flex: 1,
            padding: 'var(--spacing-md)',
          }}
        >
          {children}
        </main>

        {/* Settings modal (Cmd+, fallback) */}
        <SettingsModal
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
        />

        {/* Terminal Mode Toggle - superadmin only */}
        {isSuperAdmin && (
          <TerminalToggle
            isTerminalMode={false}
            onToggle={() => setTheme('terminal-explorer')}
          />
        )}
      </div>
    )
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--color-background)',
      }}
    >
      {/* Skip Links - WCAG 2.1 keyboard navigation */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      {showNavigation && (
        <a href="#main-navigation" className="skip-link">
          Skip to navigation
        </a>
      )}

      <Header />
      {showNavigation && (
        <Container size="xl">
          <nav id="main-navigation" aria-label="Main navigation">
            <Navigation />
          </nav>
        </Container>
      )}
      <main
        id="main-content"
        style={{
          flex: 1,
          paddingTop: 'var(--spacing-2xl)',
          paddingBottom: 'var(--spacing-2xl)',
        }}
      >
        {children}
      </main>
      <Footer />

      {/* Theme Switcher (circle button) - hidden by default for immersive themes */}
      {showThemeSwitcher && <ThemeSwitcher />}

      {/* Settings modal (Cmd+, fallback for all themes) */}
      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />

      {/* Terminal Mode Toggle - superadmin only */}
      {isSuperAdmin && (
        <TerminalToggle
          isTerminalMode={false}
          onToggle={() => setTheme('terminal-explorer')}
        />
      )}
    </div>
  )
}
